import { Controller } from "@hotwired/stimulus"

// Live "planholders coming in" feed. The count starts at 14 and ticks up as new
// planholders arrive (newest prepended at the top with a fade + green flash);
// the list is height-capped, so oldest rows fall off the bottom. Timestamps are
// generated so they always climb. Runs while the section is in view; static for
// reduced motion.
export default class extends Controller {
  static targets = ["count", "list", "source", "actions"]
  static values = {
    interval: { type: Number, default: 1500 },
    start: { type: Number, default: 4 },
    max: { type: Number, default: 20 },
    joinChance: { type: Number, default: 0.5 } // odds a given row is an "obtained" (counting) event
  }

  connect() {
    this.rows = Array.from(this.sourceTarget.content.children)
    const actions = Array.from(this.actionsTarget.content.children)
    this.joinActions = actions.filter((action) => action.dataset.counts === "true")
    this.otherActions = actions.filter((action) => action.dataset.counts !== "true")
    if (this.rows.length === 0 || actions.length === 0) return
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (this.reduced) {
      this.reset()
      return
    }

    this.active = false
    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting)
        if (visible && !this.active) {
          this.active = true
          this.reset()
          this.schedule()
        } else if (!visible && this.active) {
          this.active = false
          this.stop()
        }
      },
      { threshold: 0.2 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.stop()
    if (this.observer) this.observer.disconnect()
  }

  reset() {
    this.count = this.startValue
    this.minutes = 9 * 60 + 58 // 9:58a
    this.poolIndex = 0
    this.listTarget.replaceChildren()
    this.fill()
    this.countTarget.textContent = this.count
  }

  stop() {
    clearTimeout(this.timer)
    this.timer = null
  }

  schedule() {
    const delay = this.intervalValue * (0.75 + Math.random() * 0.5) // ~1.1–1.9s around 1.5s
    this.timer = setTimeout(() => this.tick(), delay)
  }

  tick() {
    const row = this.nextRow()
    this.listTarget.prepend(row)
    if (row.dataset.counts === "true" && this.count < this.maxValue) {
      this.count += 1
      this.countTarget.textContent = this.count
    }
    if (this.listTarget.children.length > this.capacity) this.listTarget.lastElementChild.remove()

    row.animate(
      [{ opacity: 0, transform: "translateY(-6px)" }, { opacity: 1, transform: "translateY(0)" }],
      { duration: 280, easing: "ease-out" }
    )
    row.animate(
      [{ backgroundColor: "rgba(34, 197, 94, 0.18)" }, { backgroundColor: "rgba(0, 0, 0, 0)" }],
      { duration: 1200, easing: "ease-out" }
    )

    this.schedule()
  }

  // Fill the capped list until it overflows, newest (latest time) on top.
  fill() {
    const target = (this.listTarget.clientHeight || 480) + 60
    let guard = 0
    while (this.listTarget.scrollHeight < target && guard < 60) {
      this.listTarget.prepend(this.nextRow())
      guard += 1
    }
    this.capacity = this.listTarget.children.length
  }

  nextRow() {
    const row = this.rows[this.poolIndex % this.rows.length].cloneNode(true)
    this.poolIndex += 1

    // Weighted pick: ~joinChance of rows are "obtained" (counting) events.
    const pool =
      this.joinActions.length && Math.random() < this.joinChanceValue ? this.joinActions : this.otherActions
    const action = pool[Math.floor(Math.random() * pool.length)]
    const slot = row.querySelector("[data-slot='action']")
    if (slot && action) {
      slot.replaceChildren(...action.cloneNode(true).childNodes)
      row.dataset.counts = action.dataset.counts
    }

    const date = row.querySelector("[data-field='date']")
    if (date) date.textContent = this.formatTime()
    this.minutes += 2 + Math.floor(Math.random() * 5)
    return row
  }

  formatTime() {
    let hour = Math.floor(this.minutes / 60) % 24
    const minute = String(this.minutes % 60).padStart(2, "0")
    const meridiem = hour >= 12 ? "p" : "a"
    hour %= 12
    if (hour === 0) hour = 12
    return `5/22/2026 · ${hour}:${minute}${meridiem}`
  }
}
